import { Sub } from 'features/indicationTriage/cards/ffsCalc/FfsCalcLayout'
import {
  LABEL_ALLOWABLE_STRESS,
  LABEL_CORROSION_LOCATION,
  LABEL_D_INNER,
  LABEL_D_ML,
  LABEL_D_OUTER,
  LABEL_FCA,
  LABEL_FCA_ML,
  LABEL_FOLIAS_FACTOR,
  LABEL_JOINT_EFFICIENCY,
  LABEL_L,
  LABEL_LAMBDA,
  LABEL_LMSD,
  LABEL_MAWP_DESIGN,
  LABEL_MAWP_R,
  LABEL_MAWP_RC,
  LABEL_MAWP_RL,
  LABEL_NOMINAL,
  LABEL_PRESSURE_COEFFICIENT,
  LABEL_Q,
  LABEL_QUALITY_FACTOR,
  LABEL_R_T,
  LABEL_RSF,
  LABEL_RSFA,
  LABEL_T_C_MIN,
  LABEL_T_L_MIN,
  LABEL_T_LIM,
  LABEL_T_ML,
  LABEL_TC,
  LABEL_TC_AM,
  LABEL_THICKNESS_CRITERIA,
  LABEL_TMM,
  LABEL_TMM_MINUS_FCA,
  LABEL_TRD,
  LABEL_TS_AM,
  LABEL_TSL,
} from 'features/indicationTriage/cards/ffsCalc/FfsFieldLabels'
import { MeasurementType } from 'features/indicationTriage/cards/ffsCalc/FfsParameterField'
import {
  FfsLtaReport,
  FfsPartFiveFinalAcceptabilityCriteria,
  FfsPartFiveStoredFlawSizeCriteria,
  FfsPartFourStoredCalculatedValues,
  FfsPartFourStoredThicknessCriteria,
} from 'features/indicationTriage/cards/ffsCalc/ffsStrategyContext/ffsReports'
import { formatGteThreshold } from 'features/indicationTriage/cards/ffsCalc/ffsUnits'
import { ReactNode } from 'react'
import { GeckoMeasurement } from 'types'

/**
 * Discriminated row union for the read-only report snapshot.
 * `measurement` rows carry a `GeckoMeasurement` (or null, meaning "not
 * applicable to this part/level" -- such rows are omitted, see
 * `omitEmptyMeasurementRows`). `text` rows carry a plain string (or null,
 * rendered as `—` by the modal).
 */
export type SnapshotRow =
  | {
      kind: 'measurement'
      key: string
      label: ReactNode
      measurementType: MeasurementType
      value: GeckoMeasurement | null
      trailingText?: string
    }
  | { kind: 'text'; key: string; label: ReactNode; text: string | null }

export interface SnapshotSection {
  title: string
  rows: ReadonlyArray<SnapshotRow>
}

// ---- Snapshot-local label constants (JSX). These render differently from
// their live-section counterparts (verbose vs. terse) or are report-only, so
// they intentionally stay local rather than moving to `FfsFieldLabels.tsx`.

const LABEL_CORROSION_RATE = <>Corrosion Rate</>
const LABEL_CORROSION_RATE_ML = (
  <>
    Corrosion Rate<Sub>ml</Sub>
  </>
)
const LABEL_TARGET_DATE = <>Target Date</>
const LABEL_INSPECTION_DATE = <>Inspection Date</>
const LABEL_MAWP_CALC_THICKNESS = <>Thickness for MAWP</>
const LABEL_LONGITUDINAL_FLAW_LENGTH = <>Longitudinal Flaw Length</>
const LABEL_LATITUDINAL_FLAW_LENGTH = <>Latitudinal Flaw Length</>
const LABEL_CALC_TYPE = <>Calc Type</>
const LABEL_RT_THRESHOLD = (
  <>
    R<Sub>t</Sub> threshold
  </>
)

const MAWP_CALC_THICKNESS_LABELS: Record<'t_mm' | 't_am' | 't_c', string> = {
  t_mm: 't_mm',
  t_am: 't_am',
  t_c: 't_c',
}

const CALC_TYPE_LABELS: Record<'thinWall' | 'thickWall', string> = {
  thinWall: 'Thin Wall',
  thickWall: 'Thick Wall',
}

function measurementRow(
  key: string,
  label: ReactNode,
  measurementType: MeasurementType,
  value: GeckoMeasurement | null,
  trailingText?: string
): SnapshotRow {
  return {
    kind: 'measurement',
    key,
    label,
    measurementType,
    value,
    trailingText,
  }
}

function textRow(
  key: string,
  label: ReactNode,
  text: string | null
): SnapshotRow {
  return { kind: 'text', key, label, text }
}

function getUserParametersSection(report: FfsLtaReport): SnapshotSection {
  const { user_params } = report
  return {
    title: 'User Parameters',
    rows: [
      measurementRow('fca', LABEL_FCA, 'thickness', user_params.fca),
      measurementRow('fcaMl', LABEL_FCA_ML, 'thickness', user_params.fcaMl),
      measurementRow(
        'corrosionRate',
        LABEL_CORROSION_RATE,
        'rate',
        user_params.corrosionRate
      ),
      measurementRow(
        'corrosionRateMl',
        LABEL_CORROSION_RATE_ML,
        'rate',
        user_params.corrosionRateMl
      ),
      textRow('targetDate', LABEL_TARGET_DATE, user_params.targetDate),
      textRow(
        'inspectionDate',
        LABEL_INSPECTION_DATE,
        user_params.inspectionDate
      ),
      textRow(
        'selectedMawpCalcThickness',
        LABEL_MAWP_CALC_THICKNESS,
        user_params.selectedMawpCalcThickness
          ? MAWP_CALC_THICKNESS_LABELS[user_params.selectedMawpCalcThickness]
          : null
      ),
    ],
  }
}

function getDataValuesSection(report: FfsLtaReport): SnapshotSection {
  const { data_values } = report
  return {
    title: 'Data Values',
    rows: [
      measurementRow('tmm', LABEL_TMM, 'thickness', data_values.tmm),
      measurementRow('trd', LABEL_TRD, 'thickness', data_values.trd),
      measurementRow('tc', LABEL_TC, 'thickness', data_values.tc),
      measurementRow(
        'longitudinalFlawLength',
        LABEL_LONGITUDINAL_FLAW_LENGTH,
        'thickness',
        data_values.longitudinalFlawLength
      ),
      measurementRow(
        'latitudinalFlawLength',
        LABEL_LATITUDINAL_FLAW_LENGTH,
        'thickness',
        data_values.latitudinalFlawLength
      ),
      measurementRow(
        'innerDiameter',
        LABEL_D_INNER,
        'thickness',
        data_values.innerDiameter
      ),
      measurementRow(
        'outerDiameter',
        LABEL_D_OUTER,
        'length',
        data_values.outerDiameter
      ),
      measurementRow('lambda', LABEL_LAMBDA, 'ratio', data_values.lambda),
      measurementRow(
        'nominal',
        LABEL_NOMINAL,
        'thickness',
        data_values.nominal
      ),
      measurementRow('t_ml', LABEL_T_ML, 'thickness', data_values.t_ml),
      measurementRow('d_ml', LABEL_D_ML, 'thickness', data_values.d_ml),
      textRow(
        'corrosionOnInnerSurface',
        LABEL_CORROSION_LOCATION,
        data_values.corrosionOnInnerSurface ? 'Inner surface' : 'Outer surface'
      ),
    ],
  }
}

function getMawpSection(report: FfsLtaReport): SnapshotSection {
  const { mawp } = report
  return {
    title: 'MAWP',
    rows: [
      measurementRow('e', LABEL_QUALITY_FACTOR, 'ratio', mawp.e),
      measurementRow('y', LABEL_PRESSURE_COEFFICIENT, 'ratio', mawp.y),
      measurementRow('w', LABEL_JOINT_EFFICIENCY, 'ratio', mawp.w),
      measurementRow('s_y', LABEL_ALLOWABLE_STRESS, 'pressure', mawp.s_y),
      measurementRow('tsl', LABEL_TSL, 'thickness', mawp.tsl),
      measurementRow('mawp_c', LABEL_MAWP_RC, 'pressure', mawp.mawp_c),
      measurementRow('mawp_l', LABEL_MAWP_RL, 'pressure', mawp.mawp_l),
      measurementRow(
        'mawp_design',
        LABEL_MAWP_DESIGN,
        'pressure',
        mawp.mawp_design
      ),
      measurementRow('mawpr', LABEL_MAWP_R, 'pressure', mawp.mawpr),
      textRow(
        'calcType',
        LABEL_CALC_TYPE,
        mawp.calcType ? CALC_TYPE_LABELS[mawp.calcType] : null
      ),
    ],
  }
}

function getCalculatedValuesSection(
  calculatedValues: FfsPartFourStoredCalculatedValues
): SnapshotSection {
  return {
    title: 'Calculated Values',
    rows: [
      measurementRow('rt', LABEL_R_T, 'ratio', calculatedValues.rt),
      measurementRow('rsf_a', LABEL_RSFA, 'ratio', calculatedValues.rsf_a),
      measurementRow('q', LABEL_Q, 'ratio', calculatedValues.q),
      measurementRow('l', LABEL_L, 'length', calculatedValues.l),
      measurementRow('ts_am', LABEL_TS_AM, 'thickness', calculatedValues.ts_am),
      measurementRow('tc_am', LABEL_TC_AM, 'thickness', calculatedValues.tc_am),
    ],
  }
}

function getThicknessCriteriaSection(
  thicknessCriteria: FfsPartFourStoredThicknessCriteria
): SnapshotSection {
  return {
    title: 'Thickness Criteria',
    rows: [
      measurementRow(
        't_lim',
        LABEL_T_LIM,
        'thickness',
        thicknessCriteria.t_lim
      ),
      measurementRow(
        't_c_min',
        LABEL_T_C_MIN,
        'thickness',
        thicknessCriteria.t_c_min
      ),
      measurementRow(
        't_l_min',
        LABEL_T_L_MIN,
        'thickness',
        thicknessCriteria.t_l_min
      ),
      measurementRow(
        'effective_measured_thickness',
        LABEL_THICKNESS_CRITERIA,
        'thickness',
        thicknessCriteria.effective_measured_thickness,
        formatGteThreshold(thicknessCriteria.thickness_criteria_min, {
          withUnitSuffix: true,
        })
      ),
    ],
  }
}

function getFlawSizeCriteriaSection(
  flawSizeCriteria: FfsPartFiveStoredFlawSizeCriteria
): SnapshotSection {
  return {
    title: 'Flaw Size Criteria',
    rows: [
      measurementRow(
        'rt',
        LABEL_R_T,
        'ratio',
        flawSizeCriteria.rt,
        formatGteThreshold(flawSizeCriteria.remainingThicknessThresholdRatio, {
          withUnitSuffix: true,
        })
      ),
      measurementRow(
        'tmm_threshold',
        LABEL_TMM_MINUS_FCA,
        'thickness',
        flawSizeCriteria.tmm_threshold
      ),
      measurementRow(
        'lmsd',
        LABEL_LMSD,
        'thickness',
        flawSizeCriteria.lmsd,
        formatGteThreshold(flawSizeCriteria.lmsd_threshold, {
          withUnitSuffix: true,
        })
      ),
    ],
  }
}

function getFinalAcceptabilitySection(
  finalAcceptabilityCriteria: FfsPartFiveFinalAcceptabilityCriteria
): SnapshotSection {
  return {
    title: 'Final Acceptability Criteria',
    rows: [
      measurementRow('rsf', LABEL_RSF, 'ratio', finalAcceptabilityCriteria.rsf),
      measurementRow(
        'rsf_a',
        LABEL_RSFA,
        'ratio',
        finalAcceptabilityCriteria.rsf_a
      ),
      measurementRow(
        'mt',
        LABEL_FOLIAS_FACTOR,
        'ratio',
        finalAcceptabilityCriteria.mt
      ),
      measurementRow(
        'rt_threshold',
        LABEL_RT_THRESHOLD,
        'ratio',
        finalAcceptabilityCriteria.rt_threshold
      ),
    ],
  }
}

function getResultsSection(report: FfsLtaReport): SnapshotSection {
  const { outcome } = report
  const rows: SnapshotRow[] = [
    textRow('result', 'Result', outcome.result),
    textRow('scenario', 'Scenario', outcome.scenario || null),
    textRow('recommendation', 'Recommendation', outcome.recommendation || null),
    textRow('inspector', 'Inspector', outcome.inspector || null),
    textRow('assumptions', 'Assumptions', outcome.assumptions || null),
    textRow('conclusion', 'Conclusion', outcome.conclusion || null),
  ]
  if (outcome.computedAt) {
    rows.push(textRow('computedAt', 'Computed At', outcome.computedAt))
  }
  return { title: 'Results', rows }
}

/** Omits `measurement` rows with a null value; `text` rows always render. */
function omitEmptyMeasurementRows(section: SnapshotSection): SnapshotSection {
  return {
    ...section,
    rows: section.rows.filter(
      (row) => row.kind !== 'measurement' || row.value !== null
    ),
  }
}

export function getSnapshotSections(
  report: FfsLtaReport
): ReadonlyArray<SnapshotSection> {
  // `report.ffs_part` is the broad `FfsPart` enum, not a literal per union
  // member, so it does not type-narrow -- an `in` guard on a part-specific
  // field is required instead.
  const partSections: SnapshotSection[] =
    'calculated_values' in report
      ? [
          getCalculatedValuesSection(report.calculated_values),
          getThicknessCriteriaSection(report.thickness_criteria),
        ]
      : [
          getFlawSizeCriteriaSection(report.flaw_size_criteria),
          getFinalAcceptabilitySection(report.final_acceptability_criteria),
        ]

  return [
    getUserParametersSection(report),
    getDataValuesSection(report),
    getMawpSection(report),
    ...partSections,
    getResultsSection(report),
  ].map(omitEmptyMeasurementRows)
}
